import * as React from 'react'
import { LucideIcon } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import './admin-premium.css'

export interface OverviewCardData {
  title: string
  value: string | number
  icon: LucideIcon
  trend?: {
    value: number
    label: string
    isPositive?: boolean
  }
  variant?: 'default' | 'success' | 'warning' | 'danger'
  description?: string
}

export interface OverviewCardsProps {
  cards: OverviewCardData[]
  isLoading?: boolean
  columns?: {
    default?: number
    md?: number
    lg?: number
  }
  className?: string
}

interface CardSkeletonProps {
  hasIcon?: boolean
}

function CardSkeleton({ hasIcon = true }: CardSkeletonProps) {
  return (
    <div className="stat-card-premium">
      <div className="stat-card-inner">
        <div className="flex items-start justify-between mb-4">
          <Skeleton className="h-4 w-24" />
          {hasIcon && <Skeleton className="h-10 w-10 rounded-xl" />}
        </div>
        <Skeleton className="h-10 w-28 mb-3" />
        <Skeleton className="h-4 w-32" />
      </div>
    </div>
  )
}

interface SingleCardProps {
  card: OverviewCardData
  index: number
}

function SingleCard({ card, index }: SingleCardProps) {
  const { title, value, icon: Icon, trend, variant = 'default', description } = card

  const iconColorClasses = {
    default: 'text-blue-400',
    success: 'text-emerald-400',
    warning: 'text-amber-400',
    danger: 'text-red-400',
  }

  const ArrowUpIcon = () => (
    <svg
      className="h-3 w-3"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
    </svg>
  )

  const ArrowDownIcon = () => (
    <svg
      className="h-3 w-3"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  )

  return (
    <div className="stat-card-premium" style={{ animationDelay: `${index * 0.1}s` }}>
      <div className="stat-card-inner">
        {/* Header with icon */}
        <div className="flex items-start justify-between mb-4">
          <div className="stat-label">{title}</div>
          <div className={`stat-icon-wrapper ${variant}`}>
            <Icon className={`h-5 w-5 ${iconColorClasses[variant]}`} aria-hidden="true" />
          </div>
        </div>

        {/* Main value */}
        <div className="stat-value mb-2">
          {typeof value === 'number' ? value.toLocaleString() : value}
        </div>

        {/* Description */}
        {description && (
          <p className="text-sm mb-2" style={{ color: 'var(--admin-text-secondary)' }}>
            {description}
          </p>
        )}

        {/* Trend indicator */}
        {trend && (
          <div className="flex items-center gap-2">
            <span
              className={`trend-badge ${trend.isPositive === false ? 'negative' : ''}`}
              role="status"
              aria-label={`${trend.isPositive === false ? 'Decreased' : 'Increased'} by ${Math.abs(trend.value)} percent`}
            >
              {trend.isPositive === false ? <ArrowDownIcon /> : <ArrowUpIcon />}
              {Math.abs(trend.value)}%
            </span>
            <span className="text-xs" style={{ color: 'var(--admin-text-secondary)' }}>
              {trend.label}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

export function OverviewCards({
  cards,
  isLoading = false,
  columns = { default: 1, md: 2, lg: 3 },
  className = '',
}: OverviewCardsProps) {
  const gridColsClasses = [
    columns.default ? `grid-cols-${columns.default}` : 'grid-cols-1',
    columns.md ? `md:grid-cols-${columns.md}` : 'md:grid-cols-2',
    columns.lg ? `lg:grid-cols-${columns.lg}` : 'lg:grid-cols-3',
  ].join(' ')

  if (isLoading) {
    return (
      <div className={`grid gap-6 ${gridColsClasses} ${className}`} role="status" aria-live="polite" aria-label="Loading overview cards">
        {Array.from({ length: columns.lg || 3 }).map((_, index) => (
          <CardSkeleton key={index} hasIcon={true} />
        ))}
      </div>
    )
  }

  if (!cards || cards.length === 0) {
    return (
      <div className="text-center py-8" role="status">
        <p className="text-sm" style={{ color: 'var(--admin-text-secondary)' }}>
          No data available
        </p>
      </div>
    )
  }

  return (
    <div className={`grid gap-6 ${gridColsClasses} ${className}`} role="region" aria-label="Overview statistics">
      {cards.map((card, index) => (
        <SingleCard key={`${card.title}-${index}`} card={card} index={index} />
      ))}
    </div>
  )
}
